<template>
  <div class="parent-holder" v-if="finishedLoading">
    <div class="edit-mode" v-if="isEdit === true || isEdit === 'true'">
      <b-notification
        v-if="!student.isAdminApproved"
        type="is-danger is-light"
        aria-close-label="Close notification"
        role="alert"
      >
        Student will require admin approval due to:

        <ul>
          <li
            v-for="(issue, issueKey) in student.adminStudentIssues"
            :key="issueKey"
          >
            {{ issue.message }}
            <div v-if="issue.type === 'repeatStudent' && UtilService.isAdmin()">
              <b-button
                v-for="(student, index) in issue.data"
                :key="index"
                tag="router-link"
                :to="{
                  name: 'view-student',
                  params: {
                    id: student.id,
                    applicationId: student.app_id,
                    routeIsEdit: false,
                  },
                }"
                type="is-success"
              >
                View
              </b-button>
            </div>
          </li>
        </ul>
      </b-notification>
      <b-tabs v-model="activeTab" :class="{ inactiveEditForm: isInactive }">
        <b-tab-item label="Course information" v-if="course.id">
          <div class="columns">
            <b-field label="Course" class="column is-half">
              <b-input
                disabled
                v-model="course.name"
                type="text"
                name="courseName"
              ></b-input>
            </b-field>

            <b-field label="Delivery mode" class="column is-half">
              <b-input
                disabled
                v-model="course.delivery"
                type="text"
                name="courseDelivery"
              ></b-input>
            </b-field>

            <b-field label="University" class="column is-half">
              <b-input
                disabled
                v-model="course.university"
                type="text"
                name="courseUniversity"
              ></b-input>
            </b-field>

            <b-field label="City" class="column is-half">
              <b-input
                disabled
                v-model="course.city"
                type="text"
                name="courseCity"
              ></b-input>
            </b-field>
          </div>
          <div v-if="shouldSeeChangeCourse || student.isDraft" class="columns">
            <course-list
              v-if="showUniversityChange"
              :previousStudent="true"
              @selectedCourse="selectedCourseFromList"
            ></course-list>
            <b-button
              @click="showUniversityChange = !showUniversityChange"
              type="is-primary"
              class="status-btn"
              >{{
                showUniversityChange
                  ? 'Hide uni change'
                  : 'Change student university'
              }}</b-button
            >
          </div>
        </b-tab-item>

        <b-tab-item
          label="General information"
          v-if="application.id && !student.isDraft"
        >
          <div class="columns">
            <b-field label="Updates" class="column is-half">
              <b-input
                :disabled="!isManagement"
                v-model="student.generalInfo.booking"
                type="text"
                name="booking"
              ></b-input>
            </b-field>

            <b-field label="Interview" class="column is-half">
              <b-autocomplete
                :disabled="!isManagement"
                v-model="student.generalInfo.interview"
                :data="filteredPassingData('interview')"
                open-on-focus
                @select="(option) => (selected = option)"
              >
              </b-autocomplete>
            </b-field>

            <b-field label="English test" class="column is-half">
              <b-autocomplete
                :disabled="!isManagement"
                v-model="student.generalInfo.english_test"
                :data="filteredPassingData('english_test')"
                open-on-focus
                @select="(option) => (selected = option)"
              >
              </b-autocomplete>
            </b-field>

            <b-field label="Voucher" class="column is-half">
              <b-input
                :disabled="!isManagement"
                v-model="student.generalInfo.voucher"
                type="text"
                name="voucher"
              ></b-input>
            </b-field>

            <b-field label="Student Id" class="column is-half">
              <b-input
                :disabled="!isManagement"
                v-model="student.generalInfo.studentId"
                type="text"
                name="studentId"
              ></b-input>
            </b-field>

            <b-field
              v-if="isManagement"
              label="Comments"
              class="column is-half"
            >
              <b-input
                v-model="student.generalInfo.comments"
                type="textarea"
                name="comments"
              ></b-input>
            </b-field>

            <b-field label="Student Finance" class="column is-half">
              <b-autocomplete
                :disabled="!isManagement"
                open-on-focus
                rounded
                v-model="student.generalInfo.student_finance"
                :data="filteredStudentFinance"
                icon="magnify"
                clearable
                @select="
                  (option) => (student.generalInfo.student_finance = option)
                "
              >
                <template #empty>No results found</template>
              </b-autocomplete>
            </b-field>

            <b-field label="Change application status" class="column is-half">
              <b-autocomplete
                :disabled="!isManagement"
                class="status-input"
                rounded
                v-model="application.status"
                :data="filteredDataObj"
                open-on-focus
                @select="(option) => (selected = option)"
                clearable
              >
              </b-autocomplete>
              <b-button
                :disabled="!isManagement"
                @click="saveStatus()"
                type="is-primary"
                class="status-btn"
                >Save status</b-button
              >
            </b-field>
          </div>
        </b-tab-item>

        <b-tab-item label="Initial information" class="columns">
          <initial-information
            v-if="finishedLoading"
            :studentPersonalInformation="student.personalInformation"
            :formErrors="formErrors"
            @changed="personalInfoChanged"
          ></initial-information>
        </b-tab-item>

        <b-tab-item
          label="Personal information"
          class="columns"
          :disabled="isColumnDisabled"
        >
          <personal-information
            v-if="finishedLoading"
            :studentPersonalInformation="student.personalInformation"
            :formErrors="formErrors"
            @changed="personalInfoChanged"
          ></personal-information>
        </b-tab-item>

        <b-tab-item
          label="Application information"
          class="columns"
          :disabled="isColumnDisabled"
        >
          <student-app-info
            v-if="finishedLoading"
            :studentApplcationInformation="student.applicationInformation"
            :formErrors="formErrors"
            @changed="applicationInformationChanged"
          ></student-app-info>
        </b-tab-item>

        <b-tab-item
          label="Student documents"
          class="columns document-tab"
          :disabled="isColumnDisabled"
        >
          <student-documents
            v-if="finishedLoading"
            :studentId="student.id"
            :studentDocuments="student.documents"
            :course="course"
            :disableDocsDelete="isInactive"
            @changed="documentsChanged"
            @changedDocs="documentsTriggerChange"
          ></student-documents>
        </b-tab-item>

        <b-tab-item></b-tab-item>
      </b-tabs>

      <span class="draft_status_student"
        >{{
          student.isDraft
            ? 'Draft Student'
            : !student.isApproved
            ? 'Awaiting approval'
            : 'Submitted Student'
        }}

        <span v-if="!student.studentApproved"
          >- Student has NOT approved email</span
        >
      </span>

      <div class="action-buttons">
        <b-button
          v-if="activeTab !== 0"
          @click="nextTab(false)"
          class="next-btn"
          >Previous</b-button
        >
        <b-button
          v-if="activeTab === 4 || !student.isDraft"
          @click="submitStudent(student.id === null)"
          :disabled="isQuickApplyDisabled"
          type="is-success"
          class="next-btn"
          >Submit</b-button
        >
        <b-button
          v-if="activeTab !== 5 || (activeTab !== 4 && !student.isDraft)"
          @click="nextTab(true)"
          class="next-btn"
          >Next</b-button
        >
        <b-button
          v-if="shouldSeeApprove"
          :disabled="!student.isAdminApproved && !isAdmin"
          @click="approveApplication()"
          class="next-btn"
          type="is-info"
        >
          {{
            student.isApproved
              ? 'Cancel Approve Application'
              : 'Approve Application'
          }}
        </b-button>
        <b-button
          v-if="isAdmin && !student.isAdminApproved"
          @click="adminApproval()"
          class="next-btn"
          type="is-info"
        >
          Approve Application (admin)
        </b-button>
        <b-button
          v-if="displayDraftButton"
          @click="saveStudentAsDraft()"
          class="is-warning"
          >Save draft</b-button
        >

        <b-button
          v-if="student.id && !student.studentApproved"
          @click="resendEmail()"
          class="is-danger"
          >Resend email</b-button
        >

        <b-tooltip
          type="is-light"
          :triggers="['hover', 'click']"
          multilined
          auto-close
          position="is-top"
        >
          <template v-slot:content>
            <ul>
              <li
                v-for="(document, index) in requiredDocs"
                v-bind:key="index"
                :class="document.uploaded ? 'uploaded req-doc' : 'req-doc'"
              >
                {{ document.name }}
              </li>
            </ul>
          </template>
          <b-button label="Documents" type="is-light" />
        </b-tooltip>
      </div>

      <div v-if="application.id" class="floating-menu">
        <b-icon
          @click.native.prevent="showIsMessageModalActive()"
          icon="facebook-messenger"
          size="is-small"
          type="is-white"
        ></b-icon>
        <b-tooltip
          label="Write a message"
          class="floating-menu-tooltip"
          @click.native.prevent="showIsMessageModalActive()"
        >
          2 lines
        </b-tooltip>
      </div>

      <div v-if="application.id" class="floating-menu floating-menu-2">
        <b-icon
          @click.native.prevent="isEdit = false"
          icon="book"
          size="is-small"
          type="is-white"
        ></b-icon>
        <b-tooltip
          label="Change view mode"
          class="floating-menu-tooltip"
          @click.native.prevent="isEdit = false"
        >
          2 lines
        </b-tooltip>
      </div>

      <div
        v-if="application.id && isManagement"
        class="floating-menu floating-menu-3"
      >
        <b-icon
          @click.native.prevent="isStatusModalActive = true"
          icon="view-dashboard"
          size="is-small"
          type="is-white"
        ></b-icon>
        <b-tooltip
          label="Change status"
          class="floating-menu-tooltip"
          @click.native.prevent="isStatusModalActive = true"
        >
          2 lines
        </b-tooltip>
      </div>

      <div
        v-if="application.id && isManagement"
        class="floating-menu floating-menu-4"
      >
        <b-icon
          @click.native.prevent="isStudentAdvisorChangeModalActive = true"
          icon="account-edit"
          size="is-small"
          type="is-white"
        ></b-icon>
        <b-tooltip
          label="Change student advisor"
          class="floating-menu-tooltip"
          @click.native.prevent="isStudentAdvisorChangeModalActive = true"
        >
          2 lines
        </b-tooltip>
      </div>

      <b-modal v-model="isCourseModalActive" :width="800" v-if="application.id">
        <add-course :courseId="application.course.id"></add-course>
      </b-modal>

      <b-modal
        v-model="isMessageModalActive"
        v-if="application.id"
        class="message-modal__app"
      >
        <student-message />
      </b-modal>

      <b-modal v-model="isStatusModalActive" :width="800" v-if="application.id">
        <b-field label="Change application status" class="column is-full">
          <b-autocomplete
            class="status-input"
            v-model="application.status"
            :data="filteredDataObj"
            open-on-focus
            @select="(option) => (selected = option)"
            clearable
          >
          </b-autocomplete>
          <b-button @click="saveStatus()" type="is-primary" class="status-btn"
            >Save</b-button
          >
        </b-field>
      </b-modal>

      <b-modal
        v-model="isStudentAdvisorChangeModalActive"
        :width="800"
        v-if="application.id"
      >
        <b-field
          label="Change application student advisor"
          class="column is-full"
        >
          <b-autocomplete
            name="team_leader"
            required
            placeholder="Choose from the list or type name"
            open-on-focus
            v-model="studentAdvisorName"
            :data="filteredUsers"
            icon="magnify"
            clearable
            @select="(option) => (studentAdvisorName = option)"
          >
            <template #empty>No results found</template>
          </b-autocomplete>
          <b-button
            @click="saveModeratorId()"
            type="is-primary"
            class="status-btn"
            >Save</b-button
          >
        </b-field>
      </b-modal>
    </div>

    <div class="view-mode" v-if="isEdit === false || isEdit === 'false'">
      <student-view-mode
        :studentData="student"
        :studentDocuments="studentDocuments"
        :application="application"
        :course="course"
        @changed="viewModeChange"
      ></student-view-mode>
    </div>

    <div v-if="studentDocumentsChanged.length && isManagement">
      <b-button
        label="See doc changes"
        type="is-warning"
        size="is-medium"
        class="student_changed_docs"
        @click="warningDocsChanged"
      />
    </div>

    <LoadingIcon v-if="student.isDraft && showLoader" />
  </div>
</template>

<script>
import {
  statusOptions,
  passingOptions,
  studentFinance,
  EUCountryList,
} from '../models/static';

import LoadingIcon from '../views/helpers/30secLoader.vue';

import PersonalInformation from './student/PersonalInformation.vue';
import StudentDocuments from './student/StudentDocuments.vue';
import StudentAppInfo from './student/StudentAppInfo.vue';
import StudentViewMode from './student/StudentViewMode.vue';

import StudentService from '../services/student.service';
import AddCourse from './moderator/AddCourse.vue';
import UtilService from '../services/util';

import StudentMessage from './messaging/StudentMessage.vue';
import CourseService from '../services/course.service';
import CourseList from './CourseList.vue';
import InitialInformation from './student/InitialInformation.vue';
export default {
  name: 'add-student',
  components: {
    PersonalInformation,
    StudentDocuments,
    AddCourse,
    StudentAppInfo,
    StudentViewMode,
    StudentMessage,
    CourseList,
    InitialInformation,
    LoadingIcon,
  },
  props: ['id', 'applicationId', 'routeIsEdit', 'courseId', 'localId'],
  data() {
    return {
      activeTab: 0,
      currentLocalId: 0,
      finishedLoading: false,
      showLoader: false,
      disableSubmit: false,
      showUniversityChange: false,
      student: {
        id: null,
        generalInfo: {
          booking: '',
          interview: '',
          english_test: '',
          voucher: '',
          comments: '',
          studentId: '',
          student_finance: '',
        },
        personalInformation: {
          title: '',
          first_name: '',
          last_name: '',
          date_of_birth: null,
          nationality: '',
          issueDateResidencyPermit: null,
          birth_country: '',
          phone_number: '+44',
          email: '',
          email_copy: '',
          gender: null,
          married: null,
          country: '',
          zip_code: '',
          house_number: '',
          street_address: '',
          city: '',
          delivery_mode: '',
          timetable: '',
          intake: '',
          year_of_course: '',
          emergency_contact: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          emergency_contact_relationship: '',
          interview_date_availability: '',
          student_referal: '',
          student_referal_info: '',
        },
        applicationInformation: {
          setttlement_status: '',
          employment_status: '',
          uk_entry_date: null,
          date_sfe: null,
          share_code: '',
          english_qualifications: null,
          english_exam: '',
          diploma: null,
          diploma_type: '',
          studentFinanceApplied: false,
          crn_number: '',
          disability: null,
          disability_description: '',
          criminal: null,
          criminal_description: '',
          application_route: '',
          nino_number: '',
          education_institution_name: '',
        },
        documents: {
          list: [],
          types: [],
        },
        isApproved: false,
        isDraft: true,
        isAdminApproved: true,
        adminStudentIssues: [],
      },
      documentsToBeAltered: {},
      studentDocumentsChanged: [],
      shouldChangeFiles: false,
      studentCopy: {},
      formErrors: [],
      approvedSteps: [],
      activateTabKeys: [
        'courseInfo',
        'initialInformation',
        'personalInformation',
        'applicationInformation',
        'documents',
      ],

      studentDocuments: [],

      application: {
        id: null,
        application_messages: [],
        status: '',
      },
      course: {
        id: null,
        name: '',
        documents: '',
        delivery: '',
        university: '',
        city: '',
        differentDocs: true,
      },

      studentAdvisorName: '',
      studentAdvisorId: null,
      currentCourseId: null,
      isCourseModalActive: false,
      isMessageModalActive: false,
      isStatusModalActive: false,
      isStudentAdvisorChangeModalActive: false,
      sidebarReduce: true,
      isEdit: null,
      draftTimer: null,
    };
  },
  mounted() {
    this.checkLocalId();
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    isEuStudent() {
      return this.student.personalInformation.nationality
        ? EUCountryList.includes(this.student.personalInformation.nationality)
        : true;
    },
    getActiveConversation() {
      return UtilService.getActiveConversation();
    },
    getStoreUserList() {
      return this.$store.state.users.users;
    },
    isManagement() {
      return UtilService.isManagement();
    },
    isAdmin() {
      return UtilService.isAdmin();
    },
    filteredDataObj() {
      return statusOptions.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.application.status.toLowerCase()) >= 0
        );
      });
    },
    requiredDocs() {
      return this.course.documents;
    },
    filteredUsers() {
      return this.studentAdvisorName
        ? UtilService.mappedUsernames().filter((option) => {
            return (
              option
                .toString()
                .toLowerCase()
                .indexOf(this.studentAdvisorName.toLowerCase()) >= 0
            );
          })
        : UtilService.mappedUsernames();
    },
    filteredStudentFinance() {
      if (!this.student.generalInfo.student_finance) {
        return studentFinance;
      }

      return studentFinance.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.student.generalInfo.student_finance.toLowerCase()) >=
          0
        );
      });
    },
    isColumnDisabled() {
      if (this.student.id && !this.student.isDraft) {
        return false;
      }
      return !(
        this.formErrors.filter((error) => error.step === this.currentStep)
          .length === 0 && this.approvedSteps.includes(this.currentStep)
      );
    },
    isInactive() {
      return (
        this.student.id !== null &&
        !this.isManagement &&
        this.student.isApproved
      );
    },
    isQuickApplyDisabled() {
      return this.disableSubmit;
    },
    displayDraftButton() {
      const fieldsToCheck = [
        'first_name',
        'title',
        'last_name',
        'email',
        'email_copy',
        'intake',
        'nationality',
      ];

      const allFieldsFilled = fieldsToCheck.map((field) => {
        return (
          this.student.personalInformation[field] &&
          this.student.personalInformation[field].length !== 0
        );
      });

      const completedInitialData = allFieldsFilled.every((x) => x);
      return (
        this.student.isDraft === true &&
        this.activeTab >= 1 &&
        completedInitialData
      );
    },
    shouldSeeChangeCourse() {
      return (
        UtilService.isManagement() ||
        (UtilService.isTeamLeader() && !this.student.isApproved)
      );
    },
    shouldSeeApprove() {
      return (
        (UtilService.isTeamLeader() || UtilService.isAdmin()) &&
        this.student.id !== null &&
        !this.student.isApproved &&
        !this.student.isDraft &&
        this.student.studentApproved
      );
    },
  },

  beforeDestroy() {
    clearInterval(this.draftTimer);
  },

  methods: {
    checkLocalId() {
      this.copyStudent();
      if (this.localId) {
        const personalInformation =
          this.$store.state.student.draftStudents.filter(
            (student) => student.localId === this.localId
          )[0];
        this.currentLocalId = this.localId;

        this.student = {
          ...this.student,
          ...personalInformation,
        };

        if (personalInformation) {
          this.fetchCourse(+personalInformation.courseId);
          this.isEdit = true;
        }
      }

      this.checkUrl();
    },
    checkUrl() {
      if (Number.isInteger(+this.id)) {
        this.fetchStudent(+this.id);
      } else {
        setTimeout(() => {
          this.finishedLoading = true;
        }, 10);
      }

      if (Number.isInteger(+this.courseId)) {
        this.fetchCourse(+this.courseId);
      }
    },
    selectedCourseFromList(course) {
      StudentService.updateStatus({
        id: this.application.id,
        studentId: this.student.id,
        oldCourseId: this.course.id,
        courseId: course.id,
      });
      this.$buefy.snackbar.open({
        message: 'Please refresh the page in order to see the changes',
        type: 'is-warning',
        position: 'is-top',
        indefinite: true,
      });
      this.showUniversityChange = false;
    },
    async createFile(path, name, type) {
      let response = await fetch(path);
      let data = await response.blob();
      let metadata = {
        type: type,
      };
      return new File([data], name, metadata, path);
    },
    fetchStudent(currentUrl) {
      StudentService.get(currentUrl).then((response) => {
        const parsedOptions = response.data.templateOptions;

        if (parsedOptions) {
          this.student = {
            ...parsedOptions,
            id: response.data.id,
            moderatorId: response.data.moderatorId,
            isApproved: response.data.isApproved,
            isDraft: response.data.isDraft,
            approvedAt: response.data.approvedAt,
            isAdminApproved: response.data.isAdminApproved,
            studentApproved: response.data.studentApproved,
            adminStudentIssues: response.data.adminStudentIssues
              ? JSON.parse(response.data.adminStudentIssues)
              : [],
            submittedAt: response.data.submittedAt,
            documents: {
              list: [],
              types: [],
            },
          };

          if (this.student.isDraft) {
            this.showLoader = true;
            this.setSaveAsDraft();
          }

          this.studentAdvisorId = response.data.moderatorId;
        }

        if (this.applicationId && Number.isInteger(+this.applicationId)) {
          this.fecthApplication(+this.applicationId);
        }

        this.studentDocuments = response.data.student_attachments;

        this.studentDocumentsChanged =
          response.data.student_attachments_changes;

        this.student.documents.types = this.studentDocuments.map(
          (document) => document.docType
        );

        this.studentDocuments.map(async (attachment, index) => {
          await this.createFile(
            '/' + attachment.path,
            attachment.originalName,
            attachment.type
          ).then((file) => {
            Object.defineProperty(file, 'docType', {
              value: this.student.documents.types[index],
              writable: true,
            });
            Object.defineProperty(file, 'path', {
              value: attachment.path,
              writable: true,
            });
            Object.defineProperty(file, 'type', {
              value: attachment.type,
              writable: true,
            });
            Object.defineProperty(file, 'lastModified', {
              value: new Date(attachment.updatedAt),
              writable: true,
            });
            Object.defineProperty(file, 'lastModifiedDate', {
              value: new Date(attachment.createdAt),
              writable: true,
            });
            Object.defineProperty(file, 'id', {
              value: attachment.id,
              writable: true,
            });
            this.student.documents.list.push(file);
          });
        });

        if (!this.applicationId) {
          this.finishedLoading = true;
        }

        this.fetchUsers();
      });
    },
    fecthApplication(applicationId) {
      this.finishedLoading = false;
      StudentService.getApplication(applicationId).then((response) => {
        this.application = {
          application_messages: response.data.application_messages,
          ...response.data,
        };

        this.setCourse(response.data.course);

        this.disableSubmit = JSON.parse(JSON.stringify(this.isInactive));
      });
    },
    approveApplication() {
      this.student.isApproved = true;

      this.$eventHub.$emit('socket__event', {
        messageType: 'socket__edit-application',
        messageValue: {
          value: this.student.isApproved,
          path: 'isApproved',
          studentValue: this.application.id,
          setData: true,
        },
      });

      StudentService.approveStudent({
        id: this.student.id,
        isApproved: this.student.isApproved,
        approvedAt: new Date(),
      }).then(() => {
        this.$buefy.toast.open({
          duration: 3000,
          message: this.student.isApproved
            ? 'Student was approved'
            : 'Student was rejected after approval',
          position: 'is-bottom',
          type: this.student.isApproved ? 'is-success' : 'is-warning',
        });

        this.$router.push('/applications');
      });
    },

    adminApproval() {
      StudentService.adminApproval({
        id: this.student.id,
        isAdminApproved: true,
        adminStudentIssues: null,
      }).then(() => {
        this.$buefy.toast.open({
          duration: 3000,
          message: 'Student was approved, please refresh',
          position: 'is-bottom',
          type: 'is-success',
        });
      });
    },

    resendEmail() {
      const data = {
        studentId: this.student.id,
        courseName: this.course.name,
        university: this.course.university,
        studentName: `${this.student.personalInformation.title} ${this.student.personalInformation.first_name} ${this.student.personalInformation.last_name}`,
        email: this.student.personalInformation.email,
      };

      StudentService.resendEmail(data).then(() => {
        this.$buefy.toast.open({
          duration: 3000,
          message: 'Email was resent',
          position: 'is-bottom',
          type: 'is-success',
        });
      });
    },

    filteredPassingData(type) {
      return this.student.generalInfo[type]
        ? passingOptions.filter((option) => {
            return (
              option
                .toString()
                .toLowerCase()
                .indexOf(this.student.generalInfo[type].toLowerCase()) >= 0
            );
          })
        : passingOptions;
    },

    fetchCourse(courseId) {
      this.currentCourseId = courseId;
      CourseService.get(courseId).then((response) => {
        this.setCourse(UtilService.deepCopy(response.data));
        this.disableSubmit = true;
      });
    },

    setCourse(data) {
      const template = JSON.parse(data.templateOptions);
      const uniGroupId = data.university && data.university.uniGroupRuleId;
      const docsToMap = uniGroupId
        ? data.university.universityRules.rules.documents
        : template.documents;

      const documents = docsToMap.map((doc) => {
        return {
          name: doc,
          uploaded: false,
        };
      });

      this.course = {
        id: data.id,
        name: data.title,
        documents,
        delivery: this.formatDocuments(template.timetable_options),
        university: data.university ? data.university.title : null,
        city: data.university ? this.getCity(data.university.address) : null,
        address: data.university ? data.university.address : null,
        templateOptions: data.templateOptions,
      };

      if (this.routeIsEdit !== undefined) {
        this.isEdit = this.routeIsEdit;
        this.activeTab = this.isEdit ? 1 : 0;
      } else {
        this.isEdit = true;
      }
      this.finishedLoading = true;
    },

    getCity(address) {
      return address.substring(0, address.indexOf('-'));
    },

    formatDocuments(documents) {
      return documents.join(', ');
    },

    warningDocsChanged() {
      let messages = '';
      this.studentDocumentsChanged.forEach((doc) => {
        const message = `${doc.moderatorName} has changed ${
          doc.path
        } on ${this.formatDate(doc.updatedAt)} <br>`;
        messages += message;
      });

      this.$buefy.snackbar.open({
        message: messages,
        type: 'is-warning',
        position: 'is-top',
        indefinite: true,
      });
    },

    checkIfStudentFinishedStep(step) {
      const infoKeys =
        step === 'initialInformation'
          ? [
              'first_name',
              'title',
              'last_name',
              'email',
              'email_copy',
              'intake',
              'nationality',
              !this.isEuStudent ? 'issueDateResidencyPermit' : '',
            ].filter((x) => x)
          : Object.keys(this.studentCopy[step]);
      let localStep;
      this.formErrors = [];

      if (step === 'initialInformation') {
        localStep = 'personalInformation';
        this.currentStep = step;
      } else {
        this.currentStep = step;
        localStep = step;
      }

      for (let index = 0; index < infoKeys.length; index++) {
        const fieldName = infoKeys[index];
        if (
          this.student[localStep][fieldName] ===
            this.studentCopy[localStep][fieldName] &&
          fieldName !== 'emergency_contact'
        ) {
          this.formErrors.push({
            fieldName,
            step,
            message: 'Field is required',
          });
        }
      }

      if (step === 'personalInformation') {
        const validEmail = this.validateEmail(
          this.student.personalInformation.email
        );
        const sameEmail =
          this.student.personalInformation.email ===
          this.student.personalInformation.email_copy;
        const validPhoneNumber = this.validatePhoneNumber(
          this.student.personalInformation.phone_number
        );
        if (!validEmail || !sameEmail || !validPhoneNumber) {
          this.formErrors.push({
            fieldName: 'personal_info',
            step,
            message: 'Field is required',
          });
        }

        if (
          this.formErrors.length < 3 &&
          this.student.personalInformation.student_referal !==
            'Other (explain)' &&
          this.formErrors
            .map((error) => error.fieldName)
            .includes('student_referal_info' || 'issueDateResidencyPermit')
        ) {
          this.formErrors = [];
        }
      }

      if (step === 'applicationInformation') {
        const hasDiploma = this.student.applicationInformation.diploma;
        const studentFinanceApplied =
          this.student.applicationInformation.studentFinanceApplied;
        const fieldErrors = this.formErrors.map((error) => error.fieldName);
        const emailRegex = /^(s|S)/;

        if (
          (this.formErrors.length <= 2 &&
            hasDiploma !== 'Yes' &&
            fieldErrors.includes('diploma_type')) ||
          (this.formErrors.length <= 2 &&
            studentFinanceApplied !== 'Yes' &&
            fieldErrors.includes('date_sfe'))
        ) {
          this.formErrors = [];
        }

        const validShareCode = emailRegex.test(
          this.student.applicationInformation.share_code
        );

        if (!validShareCode) {
          this.formErrors.push({
            fieldName: 'share_code',
            step,
            message: 'Share code must start with s',
          });
        }
      }

      if (!this.formErrors.length) {
        this.approvedSteps.push(step);
      }
    },

    validateEmail(email) {
      const emailRegex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return emailRegex.test(email);
    },

    validatePhoneNumber(phoneNumber) {
      return phoneNumber.toString().replace(/\D/g, '').length >= 10;
    },

    formatDate(dateString) {
      return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(dateString));
    },

    emitStudentMessage(applicationId) {
      this.$eventHub.$emit('socket__event', {
        messageType: 'socket__new-application',
        messageValue: {
          id: applicationId,
          moderatorId: this.currentUser.id,
          moderator: this.currentUser.username,
          status: 'New application',
          date: new Date(),
          studentData: this.student,
          studentId: this.student.id,
          courseData: JSON.parse(this.course.templateOptions),
          courseId: this.course.id,
          courseTitle: this.course.name,
          application_messages: [],
          university: {
            address: this.course.address,
            title: this.course.university,
          },
          team: UtilService.getUserTeam(UtilService.currentUserTeam()),
        },
      });
    },

    nextTab(isNext) {
      if (isNext) {
        const studentKey = this.activateTabKeys[this.activeTab];
        if (
          [
            'initialInformation',
            'personalInformation',
            'applicationInformation',
            'documents',
          ].includes(studentKey)
        ) {
          this.checkIfStudentFinishedStep(studentKey);
        }

        if (
          !this.formErrors.length ||
          (this.student.id && !this.student.isDraft)
        ) {
          this.activeTab++;
          if (
            this.student.isDraft &&
            studentKey === 'initialInformation' &&
            !this.application.id
          ) {
            this.saveStudentAsDraft();

            if (!this.draftTimer) {
              this.setSaveAsDraft();
            }
          }
        }
      } else {
        this.activeTab--;
      }
    },

    personalInfoChanged(value) {
      this.student.personalInformation = value.personalInformation;
    },

    viewModeChange(value) {
      this.isEdit = value.isEdit;
      this.activeTab = value.activeTab;
    },

    documentsChanged(value) {
      const compareDocs =
        JSON.stringify(value.types) !==
        JSON.stringify(this.student.documents.types);
      this.disableSubmit = null;
      this.disableSubmit = this.isInactive ? !compareDocs : this.disableSubmit;

      this.student.documents = value;
      if (this.course.documents.map) {
        this.course.documents.map((doc) => (doc.uploaded = false));
      }

      for (let index = 0; index < value['types'].length; index++) {
        const docType = value['types'][index];

        const docIndex = [...this.course.documents]
          .map((doc) => doc.name.toLowerCase())
          .indexOf(docType.toLowerCase());
        if (docIndex > -1) {
          this.course.documents[docIndex].uploaded = true;
        }
      }
    },

    documentsTriggerChange(value) {
      this.shouldChangeFiles = true;
      const foundFile = this.studentDocuments.filter(
        (file) => file.path === value.path
      );
      if (value.changedName && foundFile.length) {
        const newName = {
          name: value.value,
          id: foundFile[0].id,
        };

        if (this.documentsToBeAltered.names) {
          const findIdIndex = this.documentsToBeAltered.names.findIndex(
            (obj) => obj.id == foundFile[0].id
          );
          if (findIdIndex > -1) {
            this.documentsToBeAltered.names[findIdIndex].name = value.value;
          } else {
            this.documentsToBeAltered.names.push(newName);
          }
        } else {
          this.documentsToBeAltered.names = [newName];
        }

        return;
      }

      if (this.documentsToBeAltered.list) {
        this.documentsToBeAltered = {
          list: [...this.documentsToBeAltered.list, ...value.docs.list],
          types: [...this.documentsToBeAltered.types, ...value.docs.types],
          names: this.documentsToBeAltered.names
            ? this.documentsToBeAltered.names
            : [],
        };
      } else {
        this.documentsToBeAltered = {
          ...value.docs,
          names: this.documentsToBeAltered.names
            ? this.documentsToBeAltered.names
            : [],
        };
      }
    },

    applicationInformationChanged(value) {
      this.student.applicationInformation = value.applicationInformation;
    },
    viewCourse() {
      this.isCourseModalActive = true;
    },

    removeDraftStudent() {
      if (this.currentLocalId) {
        this.$store.dispatch('student/removeStudent', {
          localId: this.currentLocalId,
        });
      }
    },

    setSaveAsDraft() {
      this.draftTimer = setInterval(() => {
        this.showLoader = false;
        this.saveStudentAsDraft();
        this.showLoader = true;
      }, 30000);
    },

    saveStudentAsDraft() {
      this.submitStudent(this.student.id === null, true, true);
    },

    submitStudent(andApply = false, isDraft = false, isNotApprved = false) {
      let formData = new FormData();
      this.disableSubmit = true;

      const { documents, id, ...studentCopy } = this.student;
      const isApproved = isNotApprved ? false : studentCopy.isApproved;
      const shouldCheckData = {
        email: studentCopy.personalInformation.email,
        phone_number: studentCopy.personalInformation.phone_number,
        issueDateResidencyPermit:
          studentCopy.personalInformation.issueDateResidencyPermit,
      };

      formData.append('shouldCheckData', JSON.stringify(shouldCheckData));
      formData.append('templateOptions', JSON.stringify(studentCopy));
      formData.append('moderatorId', this.currentUser.id);
      formData.append('intake', studentCopy.personalInformation.intake);
      formData.append('isDraft', isDraft);
      formData.append('isAdminApproved', this.student.isAdminApproved);
      formData.append(
        'adminStudentIssues',
        JSON.stringify(this.student.adminStudentIssues)
      );
      formData.append('isApproved', isApproved);

      if (documents && documents.list.length && !this.student.id) {
        for (const [index, file] of documents.list.entries()) {
          formData.append('files', file);
          formData.append('fileTypes', documents.types[index]);
        }
      }

      if (!isDraft && !this.student.submittedAt) {
        formData.append('submittedAt', new Date());
      }

      if (this.shouldChangeFiles && this.student.id) {
        const newDocs = this.documentsToBeAltered;
        if (newDocs) {
          formData.append('fileChanges', true);
          formData.append('moderatorName', this.currentUser.username);

          if (this.documentsToBeAltered.list) {
            for (const [index, file] of newDocs.list.entries()) {
              formData.append('files', file);
              formData.append('fileTypes', newDocs.types[index]);
            }
          }

          if (this.documentsToBeAltered.names) {
            formData.append(
              'newNames',
              JSON.stringify(this.documentsToBeAltered.names)
            );
          }
        }
      }

      if (this.student.id) {
        formData.append('id', id);
      }

      formData.append('docModeratorName', this.currentUser.username);

      this.documentsToBeAltered = {};
      this.shouldChangeFiles = false;

      StudentService.create(formData)
        .then((response) => {
          if (!isDraft) {
            const message = this.student.id
              ? `Student updated succesfully`
              : `Student created succesfully`;
            this.removeDraftStudent();
            this.$buefy.toast.open({
              duration: 3000,
              message,
              position: 'is-bottom',
              type: 'is-success',
            });

            const actTaken = this.student.id ? 'edited' : 'added';
            const messageType = this.student.id
              ? 'socket__edit-student'
              : 'socket__new-student';
            this.$eventHub.$emit('socket__event', {
              messageType,
              messageValue: {
                message: `${this.currentUser.username} ${actTaken} a student for ${this.course.university}. Please refresh your app to get the new data`,
                team: UtilService.getUserTeam(UtilService.currentUserTeam()),
              },
            });

            if (this.application.id && this.student.isDraft) {
              this.$router.push(
                `/student/${this.student.id}/${this.application.id}/false`
              );
              location.reload();
            }
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: 'Student was saved as draft.',
              position: 'is-bottom',
              type: 'is-success',
            });
          }
          const valuesToUpdate = { ...isApproved, ...isDraft };
          const paths = ['isApproved', 'isDraft'];
          Object.keys(valuesToUpdate).map((value, index) => {
            const path = paths[index];
            this.$eventHub.$emit('socket__event', {
              messageType: 'socket__edit-application',
              messageValue: {
                path,
                studentValue: this.application.studentId,
                value,
                setData: true,
              },
            });

            this.$store.dispatch('application/toggleEditCell', {
              studentValue: this.application,
              path,
              value,
              setData: false,
            });
          });

          this.student.isDraft = isDraft;
          this.student.id = response.data.id
            ? response.data.id
            : this.student.id;

          if (andApply) {
            this.createStudentAndApplication(isDraft);
          } else {
            this.disableSubmit = false;
          }

          this.shouldChangeFiles = false;
        })
        .catch((error) => {
          alert(
            'There was an error submitting your student, please take a print screen and show it to someone from the technichal team',
            error
          );
          this.disableSubmit = false;
        });
    },

    createStudentAndApplication(isDraft) {
      const moderatorId = this.currentUser.id;

      if (!moderatorId) {
        return;
      }

      const data = {
        studentId: this.student.id,
        courseId: this.course.id,
        courseName: this.course.name,
        university: this.course.university,
        studentName: `${this.student.personalInformation.title} ${this.student.personalInformation.first_name} ${this.student.personalInformation.last_name}`,
        email: this.student.personalInformation.email,
        moderatorId,
        status: '',
      };

      StudentService.applyToCourse(data).then((response) => {
        this.$buefy.toast.open({
          duration: 3000,
          message: `Student applied succesfully`,
          position: 'is-bottom',
          type: 'is-success',
        });

        const applicationId = response.data.data.id;
        this.emitStudentMessage(applicationId);

        this.$router.push(
          `/student/${this.student.id}/${applicationId}/${isDraft}`
        );
        this.$router.go(this.$router.currentRoute);
      });
    },

    fetchUsers() {
      const studentAdvisorName = this.getStoreUserList.filter(
        (leader) => leader.id === this.studentAdvisorId
      );

      if (studentAdvisorName.length) {
        this.studentAdvisorName = studentAdvisorName[0].username;
      }
    },

    saveModeratorId() {
      const studentAdvisor = this.getStoreUserList.filter(
        (leader) => leader.username === this.studentAdvisorName
      );
      if (studentAdvisor.length) {
        const moderatorId = studentAdvisor[0].id;
        const id = this.student.id;

        StudentService.updateStudentAdvisor({
          studentId: id,
          moderatorId: moderatorId,
          applicationId: this.application.id,
        }).then(() => {
          this.$buefy.toast.open({
            duration: 3000,
            message:
              'Student advisor was changed succesfully, please refresh to see the changes',
            position: 'is-bottom',
            type: 'is-success',
          });
        });
      }
    },

    showIsMessageModalActive() {
      this.isMessageModalActive = true;
      this.$store.dispatch('application/setActiveConversation', {
        id: this.application.id,
        studentId: this.application.studentId,
        courseDocuments: this.course.documents.map((doc) => doc.name),
        studentName: UtilService.studentName(this.student.personalInformation),
        courseTitle: this.course.name,
        moderatorId: this.application.moderatorId,
        isApproved: this.student.isApproved,
      });
    },

    saveStatus() {
      StudentService.updateStatus({
        id: this.application.id,
        status: this.application.status,
      }).then(() => {
        this.$buefy.toast.open({
          duration: 3000,
          message: `Status updated succesfully`,
          position: 'is-bottom',
          type: 'is-success',
        });

        this.$eventHub.$emit('socket__event', {
          messageType: 'socket__edit-application',
          messageValue: {
            value: this.application.status,
            path: 'status',
            studentValue: this.application.id,
            setData: true,
          },
        });
      });
    },

    copyStudent() {
      this.studentCopy = {
        ...this.student,
      };
      delete this.studentCopy.personalInformation.timetable;
      delete this.studentCopy.applicationInformation.criminal_description;
      delete this.studentCopy.applicationInformation.disability_description;
      delete this.studentCopy.applicationInformation.crn_number;
      delete this.studentCopy.applicationInformation.english_exam;
    },
  },
};
</script>

<style>
.message-modal__app .animation-content.modal-content {
  width: calc(100vw - 100px);
  height: calc(100vh - 80px);
  display: flex;
  justify-content: space-between;
}

.new-message {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-wrap: wrap;
}

.toggle-messages {
  position: absolute;
  top: 10px;
  right: 0;
}

.columns {
  flex-wrap: wrap;
}

.tabs li.is-active a,
.tabs li a:hover,
.tabs li.is-active a span {
  text-decoration: none;
}

.custom-autocomplete {
  height: fit-content;
}

.inactiveEditForm .tab-content .tab-item.columns:not(.document-tab) {
  pointer-events: none;
}

.action-buttons {
  justify-content: center;
  display: flex;
}

.action-buttons button {
  margin: 0 10px;
}

.parent-holder {
  margin-bottom: 40px;
}

.card-holder {
  margin-top: 30px;
  margin-left: 0px;
}

.animation-content.modal-content {
  height: 400px;
}

.status-btn {
  margin-left: 15px;
}

.status-input {
  width: 400px;
}

.message span.icon {
  margin: 0 12px;
}

.b-sidebar .sidebar-content {
  padding: 12px;
}

.modal-content {
  padding: 12px;
}

nav.tabs {
  scrollbar-width: 20px;
  scrollbar-color: #666;
  /* Chrome, Edge, and Safari */
}

nav.tabs::-webkit-scrollbar {
  width: 16px;
  height: 12px;
}

nav.tabs::-webkit-scrollbar-track {
  background: #fff;
}

.req-doc {
  color: red;
}

.req-doc::before {
  content: '❌';
}

.req-doc.uploaded {
  color: #48c78e;
}

.req-doc.uploaded::before {
  content: '✔️';
}

nav.tabs::-webkit-scrollbar-thumb {
  background-color: #666;
  border-radius: 10px;
}

nav.tabs li {
  border-bottom: none;
}

nav.tabs li a {
  border-bottom: none;
  font-size: 15px;
  font-weight: 600;
}

nav.tabs li.is-active a {
  color: #ffc451;
}

nav.tabs li a:not([href]):not([class]):hover {
  color: #ffc451;
}

.floating-menu {
  position: fixed;
  bottom: 20px;
  left: 20px;
  background: #8c67ef;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15), 0px 4px 8px rgba(0, 0, 0, 0.2);
  padding: 8px 12px;
}

.floating-menu.floating-menu-2 {
  left: 80px;
}

.floating-menu.floating-menu-3 {
  left: 140px;
}

.floating-menu.floating-menu-4 {
  left: 200px;
}

.b-tooltip.floating-menu-tooltip {
  position: absolute;
  left: 50%;
  color: transparent;
  top: 50%;
  transform: translate(-50%, -50%);
}

button.button.student_changed_docs {
  position: fixed;
  bottom: 20px;
  right: 0;
}

.edit-mode {
  position: relative;
}

.draft_status_student {
  background: #7e7a7a;
  color: #ffc451;
  border-radius: 8px;
  position: absolute;
  top: -10px;
  right: 0;
  padding: 8px 12px;
}
</style>
